// src/utils/exportToExcel.ts
import * as XLSX from 'xlsx';
import type { Ticket } from '@/types/ticket.types';

export interface ExportTicketData {
    'Αριθμός Αιτήματος': string;
    'Τίτλος': string;
    'Κατάσταση': string;
    'Προτεραιότητα': string;
    'Εταιρεία': string;
    'Τμήμα': string;
    'Κατηγορία': string;
    'Υπεύθυνος': string;
    'Δημιουργήθηκε': string;
    'Ημ/νία Πρώτης Απάντησης': string;
    'Τελευταία Ενημέρωση': string;
    'Ημ/νία Κλεισίματος': string;
    'Περιγραφή': string;
}

/**
 * Exports tickets to Excel file
 * @param tickets - Array of tickets to export
 * @param filename - Optional filename (defaults to 'tickets_export_YYYY-MM-DD.xlsx')
 */
export const exportTicketsToExcel = (tickets: Ticket[], filename?: string) => {
    // Map tickets to export format with Greek column headers
    const exportData: ExportTicketData[] = tickets.map(ticket => ({
        'Αριθμός Αιτήματος': `#${ticket.ticket_number}`,
        'Τίτλος': ticket.title,
        'Κατάσταση': ticket.status.status_name,
        'Προτεραιότητα': ticket.priority.priority_name,
        'Εταιρεία': ticket.company.company_name,
        'Τμήμα': ticket.department.department_name,
        'Κατηγορία': ticket.category.category_name,
        'Υπεύθυνος': ticket.assignee 
            ? `${ticket.assignee.first_name} ${ticket.assignee.last_name}` 
            : 'Μη ανατεθειμένο',
        'Δημιουργήθηκε': formatDateForExcel(ticket.created_at),
        'Ημ/νία Πρώτης Απάντησης': ticket.responded_at ? formatDateForExcel(ticket.responded_at) : '-',
        'Τελευταία Ενημέρωση': formatDateForExcel(ticket.updated_at),
        'Ημ/νία Κλεισίματος': ticket.closed_at ? formatDateForExcel(ticket.closed_at) : '-',
        'Περιγραφή': ticket.description,
    }));

    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    
    // Convert data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths for better readability
    const columnWidths = [
        { wch: 15 },  // Αριθμός Αιτήματος
        { wch: 30 },  // Τίτλος
        { wch: 15 },  // Κατάσταση
        { wch: 15 },  // Προτεραιότητα
        { wch: 25 },  // Εταιρεία
        { wch: 20 },  // Τμήμα
        { wch: 20 },  // Κατηγορία
        { wch: 20 },  // Υπεύθυνος
        { wch: 18 },  // Δημιουργήθηκε
        { wch: 18 },  // Ημ/νία Πρώτης Απάντησης
        { wch: 18 },  // Τελευταία Ενημέρωση
        { wch: 18 },  // Ημ/νία Κλεισίματος
        { wch: 50 },  // Περιγραφή
    ];
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Αιτήματα');

    // Generate filename with current date if not provided
    const exportFilename = filename || `tickets_export_${getCurrentDateString()}.xlsx`;

    // Write the file
    XLSX.writeFile(workbook, exportFilename);
};

/**
 * Formats a date string for Excel export
 */
const formatDateForExcel = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Gets current date as formatted string for filename
 */
const getCurrentDateString = (): string => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
};
